import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BiPlus, BiTrash } from "react-icons/bi";

import { API_BASE_URL } from '../utils/urls.js';
import axiosClient from "../utils/apiClient.js";

const ServerTimeEntryList = () => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const user = JSON.parse(localStorage.getItem("user"));
  const isAdmin = user?.roleName === "admin";
  const isCaseManager = user?.roleName === "case_manager";
  const isFieldAgent = user?.roleName === "field_agent";
  const isSevers = user?.roleName === "servers";

  const canViewAllEntries = isAdmin || isCaseManager;
  const canDelete = isAdmin || isCaseManager || isSevers;

  useEffect(() => {
    const fetchTimeEntries = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        let response;
        if (canViewAllEntries) {
          // Admins and Case Managers can see all entries
          response = await axiosClient.get(`${API_BASE_URL}/time-entries/all`); 
        } else if (isSevers || isFieldAgent) {
          // Servers and Field Agents see their own entries
          response = await axiosClient.get(
            `${API_BASE_URL}/time-entries/user/${user?.id}`
          );
        } else {
          setError("You don't have permission to view this page.");
          setLoading(false);
          return;
        }
        setTimeEntries(response.data);
      } catch (err) {
        console.error("Error fetching time entries:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load time entries. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTimeEntries();
  }, [user?.id, canViewAllEntries, isFieldAgent, isSevers]);

  // Filter time entries based on search query
  const filteredEntries = timeEntries.filter((entry) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const caseName = (entry.case_name || "").toLowerCase();
    const userName = `${entry.first_name || ""} ${entry.last_name || ""}`.toLowerCase();
    const desc = (entry.description || "").toLowerCase();
    return caseName.includes(q) || userName.includes(q) || desc.includes(q);
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredEntries.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (
      !window.confirm(
        `Are you sure you want to delete the ${selectedIds.length} selected time entry/entries?`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    try {
      await axiosClient.post(`${API_BASE_URL}/time-entries/bulk-delete`, {
        ids: selectedIds,
      });
      setTimeEntries((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error("Bulk delete error, attempting fallback:", err);
      try {
        await Promise.all(
          selectedIds.map((id) =>
            axiosClient.delete(`${API_BASE_URL}/time-entries/${id}`)
          )
        );
        setTimeEntries((prev) => prev.filter((entry) => !selectedIds.includes(entry.id)));
        setSelectedIds([]);
      } catch (fallbackErr) {
        setError(
          err.response?.data?.message || "Failed to delete selected time entries."
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (window.confirm("Are you sure you want to delete this time entry?")) {
      try {
        await axiosClient.delete(`${API_BASE_URL}/time-entries/${entryId}`);
        setTimeEntries((prev) => prev.filter((entry) => entry.id !== entryId));
        setSelectedIds((prev) => prev.filter((id) => id !== entryId));
      } catch (err) {
        console.error("Error deleting time entry:", err);
        setError(err.response?.data?.message || "Failed to delete time entry.");
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">
        Loading time entries...
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  const totalHours = filteredEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.hours) || 0),
    0
  );

  const totalExpenses = filteredEntries.reduce(
    (sum, entry) => sum + (parseFloat(entry.expense_amount) || 0),
    0
  );

  const isAllSelected =
    filteredEntries.length > 0 && selectedIds.length === filteredEntries.length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Time & Expenses</h1>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search entries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-2 border rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
          />
          {(isAdmin || isCaseManager || isFieldAgent || isSevers) && (
            <Link
              to="/server/time-entries/new"
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
            >
              <BiPlus className="mr-2" /> New Entry
            </Link>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <p className="text-gray-500 text-xs">Total Hours</p>
          <p className="text-xl font-semibold">{totalHours.toFixed(2)}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <p className="text-gray-500 text-xs">Total Expenses</p>
          <p className="text-xl font-semibold">${totalExpenses.toFixed(2)}</p>
        </div>
      </div>

      {/* Bulk actions banner */}
      {canDelete && selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3 transition-all">
          <div className="flex items-center space-x-2 text-sm text-blue-800 font-medium">
            <span>
              {selectedIds.length} of {filteredEntries.length} selected
            </span>
            <button
              onClick={() => setSelectedIds([])}
              className="text-xs text-blue-600 underline hover:text-blue-800 ml-2"
            >
              Clear Selection
            </button>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-md shadow-sm transition-colors cursor-pointer"
          >
            <BiTrash className="w-4 h-4" />
            <span>
              {isDeleting ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
            </span>
          </button>
        </div>
      )}

      {/* MOBILE VIEW: Card Layout */}
      <div className="space-y-3 block sm:hidden">
        {filteredEntries.length > 0 ? (
          filteredEntries.map((entry) => {
            const isSelected = selectedIds.includes(entry.id);
            return (
              <div
                key={entry.id}
                className={`bg-white border rounded-lg shadow-sm p-4 space-y-2 ${
                  isSelected ? "bg-blue-50/50 border-blue-300" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  {canDelete && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelect(entry.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer mr-2"
                    />
                  )}
                  <p className="text-sm text-gray-700 font-semibold flex-1">
                    {entry.case_name || "No Case Name"}
                  </p>
                </div>

                {canViewAllEntries && (
                  <p className="text-xs text-gray-500">
                    Field Agent: {entry.first_name} {entry.last_name}
                  </p>
                )}

                <p className="text-xs text-gray-500">
                  Date: {new Date(entry.entry_date).toLocaleDateString()}
                </p>

                <p className="text-xs text-gray-500">Hours: {entry.hours}</p>
                <p className="text-xs text-gray-500">
                  Expenses: ${parseFloat(entry.expense_amount || 0).toFixed(2)}
                </p>

                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {entry.description}
                </p>

                {canDelete && (
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="text-red-600 text-sm flex items-center gap-1 pt-1"
                  >
                    <BiTrash /> Delete
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-center text-gray-500">No time entries found.</p>
        )}
      </div>

      {/* DESKTOP TABLE VIEW */}
      <div className="hidden sm:block bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              {canDelete && (
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Case</th>
              {canViewAllEntries && <th className="px-4 py-3 text-left">Field Agent</th>}
              <th className="px-4 py-3 text-left">Hours</th>
              <th className="px-4 py-3 text-left">Expenses</th>
              <th className="px-4 py-3 text-left">Description</th>
              {canDelete && <th className="px-4 py-3"></th>}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {filteredEntries.length > 0 ? (
              filteredEntries.map((entry) => {
                const isSelected = selectedIds.includes(entry.id);
                return (
                  <tr
                    key={entry.id}
                    className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50/50" : ""}`}
                  >
                    {canDelete && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(entry.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3">{new Date(entry.entry_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3">{entry.case_name}</td>

                    {canViewAllEntries && (
                      <td className="px-4 py-3">
                        {entry.first_name} {entry.last_name}
                      </td>
                    )}

                    <td className="px-4 py-3">{entry.hours}</td>
                    <td className="px-4 py-3">${parseFloat(entry.expense_amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-600">{entry.description}</td>

                    {canDelete && (
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <BiTrash />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={canDelete ? (canViewAllEntries ? "8" : "7") : canViewAllEntries ? "7" : "6"}
                  className="px-4 py-4 text-center text-gray-500"
                >
                  No time entries found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServerTimeEntryList;
